// frontend/src/components/inventario/InventarioDonut.tsx
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { InventarioRow } from '@/types'
import { sumUnitsByAlmacen, toDonutSlices } from '@/utils/inventarioAgg'

const PALETTE = ['#0ea5e9', '#10b981', '#f59e0b', '#f43f5e', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316']
const OTROS_COLOR = '#94a3b8'
const MAX_SLICES = 8
const DURATION = 900

/** Número completo con separador de millar (10.500, no «10,5k»). */
function fmtUds(n: number) {
  return n.toLocaleString('es-ES', { maximumFractionDigits: 0 })
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Reloj de animación 0→1 por rAF durante `durationMs`. Si reduced-motion, queda en 1. */
function useAnimationClock(durationMs: number): number {
  const [p, setP] = useState(() => (prefersReducedMotion() ? 1 : 0))
  useEffect(() => {
    if (prefersReducedMotion()) { setP(1); return }
    let raf = 0
    let start = 0
    const tick = (ts: number) => {
      if (!start) start = ts
      const t = Math.min(1, (ts - start) / durationMs)
      setP(t)
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [durationMs])
  return p
}

export function InventarioDonut({ dark, rows }: { dark: boolean; rows: InventarioRow[] }) {
  const { t } = useTranslation()
  const otrosLabel = t('inventory.donut.otros')
  const slices = useMemo(
    () => toDonutSlices(sumUnitsByAlmacen(rows), MAX_SLICES, otrosLabel),
    [rows, otrosLabel],
  )
  const p = useAnimationClock(DURATION)

  if (!slices.length) return null

  const grandTotal = slices.reduce((s, x) => s + x.total, 0)
  const colorOf = (i: number, almacen: string) =>
    almacen === otrosLabel ? OTROS_COLOR : PALETTE[i % PALETTE.length]

  // Geometría
  const SIZE = 200, cx = SIZE / 2, cy = SIZE / 2, r = 70, stroke = 26
  const C = 2 * Math.PI * r

  let acc = 0
  const arcs = slices.map((s, i) => {
    const startFrac = acc
    const lenFrac = s.pct / 100
    acc += lenFrac
    const revealedFrac = Math.max(0, Math.min(lenFrac, p - startFrac)) // barrido horario
    return { ...s, i, startFrac, lenFrac, revealedFrac }
  })

  // Key insights: nº almacenes que concentran ≥80%
  const top = slices[0]
  let cum = 0, nConc = 0
  for (const s of slices) {
    cum += s.pct
    if (s.almacen !== otrosLabel) nConc++
    if (cum >= 80) break
  }

  const cardBg  = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200'
  const txtMainHex = dark ? '#e2e8f0' : '#1e293b'
  const ringBg  = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'
  const insightsOpacity = Math.max(0, Math.min(1, (p - 0.85) / 0.15))

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${cardBg}`}>
      <div className={`text-[10px] font-bold uppercase tracking-widest mb-3 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
        {t('inventory.donut.title')}
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-center">
        {/* Donut */}
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE} height={SIZE} className="shrink-0">
          <g transform={`rotate(-90 ${cx} ${cy})`}>
            <circle cx={cx} cy={cy} r={r} fill="none" stroke={ringBg} strokeWidth={stroke} />
            {arcs.map(a => (
              <circle
                key={a.almacen}
                cx={cx} cy={cy} r={r}
                fill="none"
                stroke={colorOf(a.i, a.almacen)}
                strokeWidth={stroke}
                strokeLinecap="butt"
                strokeDasharray={`${a.revealedFrac * C} ${C}`}
                transform={`rotate(${a.startFrac * 360} ${cx} ${cy})`}
              />
            ))}
          </g>
          <text x={cx} y={cy - 2} textAnchor="middle" fontSize={22} fontWeight={700} fill={txtMainHex} fontFamily="inherit">
            {fmtUds(Math.round(grandTotal * p))}
          </text>
          <text x={cx} y={cy + 15} textAnchor="middle" fontSize={10} fill={dark ? '#64748b' : '#94a3b8'} fontFamily="inherit">
            {t('inventory.donut.unitsCenter')}
          </text>
        </svg>

        {/* Leyenda */}
        <ul className="flex-1 w-full flex flex-col gap-1.5 min-w-0">
          {arcs.map(a => {
            const shown = a.lenFrac > 0 ? Math.min(1, a.revealedFrac / a.lenFrac) : 1
            return (
              <li key={a.almacen} className="flex items-center gap-2 text-[12px]">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: colorOf(a.i, a.almacen) }} />
                <span className={`truncate ${dark ? 'text-slate-300' : 'text-slate-600'}`} title={a.almacen}>
                  {a.almacen.length > 18 ? `${a.almacen.slice(0, 18)}…` : a.almacen}
                </span>
                <span className={`ml-auto tabular-nums font-semibold ${dark ? 'text-slate-200' : 'text-slate-700'}`}>
                  {(a.pct * shown).toFixed(1)}%
                </span>
              </li>
            )
          })}
        </ul>
      </div>

      {/* Key Insights */}
      <div
        className={`mt-3 pt-3 border-t ${dark ? 'border-white/8' : 'border-slate-200'}`}
        style={{ opacity: insightsOpacity, transition: 'opacity 200ms ease' }}
      >
        <div className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
          {t('inventory.donut.insightsTitle')}
        </div>
        <ul className={`flex flex-col gap-1 text-[12px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
          <li>→ {t('inventory.donut.insightTop', { pct: top.pct.toFixed(1), almacen: top.almacen })}</li>
          <li>→ {t('inventory.donut.insightConcentration', { count: nConc, n: nConc, pct: 80 })}</li>
          <li>→ {t('inventory.donut.insightTotal', { total: fmtUds(grandTotal), n: slices.length })}</li>
        </ul>
      </div>
    </div>
  )
}
